import React, { useCallback, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../utils/classNames";
import UserCardAvatar from "../UserCardAvatar";
import EmojiPicker from "../EmojiPicker";
import Button from "../../atoms/Button";

import "./index.scss";

const CommentComposer = React.forwardRef(({
    viewerId,
    viewerName,
    viewerAvatarUrl,
    value,
    onChange,
    onKeyDown,
    onSubmit,
    placeholder,
    submitLabel,
    disabled,
    replyTo,
    onCancelReply,
    className,
}, ref) => {
    const { t } = useTranslation();
    const textareaRef = useRef(null);
    const trimmed = (value || "").trim();
    const canSubmit = !disabled && trimmed.length > 0;
    const resolvedPlaceholder = placeholder || t("home.postComments.placeholder");
    const resolvedSubmitLabel = submitLabel || t("home.postComments.submit");

    useImperativeHandle(ref, () => ({
        focus: () => {
            textareaRef.current?.focus();
        },
    }), []);

    const handleEmojiSelect = useCallback(
        (emoji) => {
            const native = emoji?.native;
            if (!native) {
                return;
            }
            onChange?.(`${value || ""}${native}`);
        },
        [onChange, value],
    );

    const handleSubmitClick = () => {
        if (!canSubmit) {
            return;
        }
        onSubmit?.();
    };

    return (
        <div className={classNames("comment-composer", className)}>
            <div className="comment-composer__avatar">
                <UserCardAvatar
                    userId={viewerId || undefined}
                    username={viewerName || ""}
                    avatarUrl={viewerAvatarUrl || undefined}
                    alt={viewerName || ""}
                    className="comment-composer__avatar-media"
                    avatarClassName="comment-composer__avatar-image"
                    enableProfilePreview={false}
                />
            </div>

            <div className="comment-composer__field">
                {replyTo && (
                    <div className="comment-composer__reply-banner">
                        <span>{t("home.postComments.replyingTo", { author: replyTo.authorName })}</span>
                        <button
                            type="button"
                            onClick={onCancelReply}
                            aria-label={t("home.postComments.cancelReply")}
                        >
                            ×
                        </button>
                    </div>
                )}
                <textarea
                    ref={textareaRef}
                    className="comment-composer__input"
                    value={value}
                    onChange={(event) => onChange?.(event.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={resolvedPlaceholder}
                    rows={1}
                    disabled={disabled}
                />
                <div className="comment-composer__bar">
                    <div className="comment-composer__tools">
                        <EmojiPicker
                            onSelectEmoji={handleEmojiSelect}
                            iconAlt={t("home.postComments.composerEmojiAria")}
                            zIndex={7300}
                            anchorSelector=".comment-composer__field"
                        />
                    </div>
                    <Button
                        variant="gradient"
                        size="small"
                        shape="pill"
                        type="button"
                        disabled={!canSubmit}
                        onClick={handleSubmitClick}
                        className="comment-composer__submit"
                    >
                        {resolvedSubmitLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
});

CommentComposer.displayName = "CommentComposer";

CommentComposer.propTypes = {
    viewerId: PropTypes.string,
    viewerName: PropTypes.string,
    viewerAvatarUrl: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onKeyDown: PropTypes.func,
    onSubmit: PropTypes.func,
    placeholder: PropTypes.string,
    submitLabel: PropTypes.string,
    disabled: PropTypes.bool,
    replyTo: PropTypes.shape({
        commentId: PropTypes.string.isRequired,
        authorName: PropTypes.string.isRequired,
        authorUsername: PropTypes.string.isRequired,
    }),
    onCancelReply: PropTypes.func,
    className: PropTypes.string,
};

CommentComposer.defaultProps = {
    viewerId: null,
    viewerName: "",
    viewerAvatarUrl: null,
    value: "",
    onChange: () => {},
    onKeyDown: () => {},
    onSubmit: () => {},
    placeholder: "",
    submitLabel: "",
    disabled: false,
    replyTo: null,
    onCancelReply: () => {},
    className: "",
};

export default CommentComposer;
